"use client";

import type { ReactNode } from "react";
import { useTranslations, useLocale } from "use-intl";

import { BioText } from "@/components/bio-text";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EntityHero } from "@/components/catalog-entity/entity-hero";
import { EntityQuickLinks } from "@/components/catalog-entity/entity-quick-links";
import { CountryFlag } from "@/components/country-flag";
import { FallbackImage } from "@/components/fallback-image";
import { GenreStyleTags } from "@/components/genre-style-tags";
import { Link } from "@/components/link";
import { RichTextHtml } from "@/components/rich-text-html";
import { ShareButton } from "@/components/share-button";
import { ArtistFollowButton } from "@/components/social/artist-follow-button";
import { WikipediaSection } from "@/components/wikipedia-section";
import { useSession } from "@/hooks/use-session";
import type { ArtistIndexBreadcrumb, TaxonomyPageLink } from "@garimpa/api/music/types";
import { formatCountry } from "@garimpa/lib/countries";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";
import type { CatalogUrl, SocialLink } from "@garimpa/lib/musicTypes";

interface ArtistHeaderProps {
  artistId: string;
  name: string;
  type?: string | null;
  ended?: boolean | null;
  beginYear?: number | null;
  endYear?: number | null;
  realname?: string | null;
  country?: string | null;
  originAreaName?: string | null;
  imageUrl: string | null;
  bioText: string | null;
  bioHtml: string | null;
  wikidataId?: string | null;
  officialUrls: CatalogUrl[];
  socialUrls: SocialLink[];
  genres?: string[] | null;
  styles?: string[] | null;
  taxonomyPages?: TaxonomyPageLink[] | null;
  primaryIndex: ArtistIndexBreadcrumb | null;
  /** Secondary knowledge (members, similar artists, links) beside or below the identity. */
  aside?: ReactNode;
}

function ArtistIdentityChips({
  activeLabel,
  endedLabel,
  showEndedStatus,
  typeLabel,
}: {
  activeLabel: string | null;
  endedLabel: string;
  showEndedStatus: boolean;
  typeLabel: string | null;
}) {
  if (![typeLabel, activeLabel, showEndedStatus].some(Boolean)) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {typeLabel && (
        <span className="rounded bg-secondary px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-foreground">
          {typeLabel}
        </span>
      )}
      {activeLabel && (
        <span className="rounded bg-primary/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-primary">
          {activeLabel}
        </span>
      )}
      {showEndedStatus && (
        <span className="rounded bg-destructive/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-destructive">
          {endedLabel}
        </span>
      )}
    </div>
  );
}

function ArtistIdentityDetails({
  country,
  countryName,
  originAreaName,
  realname,
}: Pick<ArtistHeaderProps, "country" | "originAreaName" | "realname"> & {
  countryName: string | null;
}) {
  if (!realname && !countryName) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground md:text-[15px]">
      {realname && <span>{realname}</span>}
      {realname && countryName && <span aria-hidden="true">·</span>}
      {countryName && (
        <span className="flex items-center gap-1.5">
          <CountryFlag code={country} decorative />
          {countryName}
          {originAreaName && <span>· {originAreaName}</span>}
        </span>
      )}
    </p>
  );
}

function ArtistBiography({
  bioHtml,
  bioText,
  wikidataId,
}: Pick<ArtistHeaderProps, "bioHtml" | "bioText" | "wikidataId">) {
  if (bioHtml) return <RichTextHtml safeHtml={bioHtml} maxLines={3} />;
  if (bioText) return <BioText text={bioText} maxLines={3} />;
  return <WikipediaSection wikidataId={wikidataId} />;
}

export function ArtistHeader({
  artistId,
  name,
  type,
  ended,
  beginYear,
  endYear,
  realname,
  country,
  originAreaName,
  imageUrl,
  bioText,
  bioHtml,
  wikidataId,
  officialUrls,
  socialUrls,
  genres,
  styles,
  taxonomyPages,
  primaryIndex,
  aside,
}: ArtistHeaderProps) {
  const t = useTranslations("artist");
  const tCommon = useTranslations("globals.common");
  const locale = useLocale();
  const { data: session } = useSession();
  const countryName = country ? formatCountry(country, locale) : null;
  const typeLabel = formatArtistType(type, t);
  const activeLabel = formatActiveRange(beginYear, endYear, type, t);
  const showEndedStatus = ended === true && isArtistGroupLike(type);

  return (
    <>
      <Breadcrumbs className="mb-4" label={tCommon("breadcrumbNavigation")}>
        {primaryIndex ? (
          <Link
            to={primaryIndex.path}
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            {primaryIndex.label}
          </Link>
        ) : null}
        <span className="font-medium text-primary">{name}</span>
      </Breadcrumbs>

      <EntityHero
        title={name}
        media={
          <FallbackImage
            src={withImageRenderParams(imageUrl, { width: 320 })}
            srcSet={buildImageSrcSet(imageUrl, [150, 320])}
            alt={name}
            fallback="person"
            priority
            className="rounded-full"
            sizes="(min-width: 768px) 136px, 80px"
          />
        }
        chips={
          <ArtistIdentityChips
            activeLabel={activeLabel}
            endedLabel={t("statusEnded")}
            showEndedStatus={showEndedStatus}
            typeLabel={typeLabel}
          />
        }
        meta={
          <ArtistIdentityDetails
            country={country}
            countryName={countryName}
            originAreaName={originAreaName}
            realname={realname}
          />
        }
        actions={
          <>
            {session && (
              <ArtistFollowButton artistId={artistId} artistName={name} className="shrink-0" />
            )}
            <ShareButton title={name} className="shrink-0 [&_.share-button-label]:hidden" />
            <EntityQuickLinks officialUrls={officialUrls} socialUrls={socialUrls} />
          </>
        }
        body={<ArtistBiography bioHtml={bioHtml} bioText={bioText} wikidataId={wikidataId} />}
        footer={
          <GenreStyleTags genres={genres} styles={styles} pages={taxonomyPages} className="mb-0" />
        }
        aside={aside}
      />
    </>
  );
}

function isArtistGroupLike(type: string | null | undefined) {
  switch (type?.toLowerCase()) {
    case "group":
    case "orchestra":
    case "choir":
      return true;
    default:
      return false;
  }
}

function artistDateLabelType(type: string | null | undefined) {
  if (type?.toLowerCase() === "person") return "person";
  if (isArtistGroupLike(type)) return "group";
  return "other";
}

function formatArtistType(
  type: string | null | undefined,
  t: ReturnType<typeof useTranslations<"artist">>,
) {
  switch (type?.toLowerCase()) {
    case "person":
      return t("artistTypePerson");
    case "group":
      return t("artistTypeGroup");
    case "orchestra":
      return t("artistTypeOrchestra");
    case "choir":
      return t("artistTypeChoir");
    case "character":
      return t("artistTypeCharacter");
    case "other":
      return t("artistTypeOther");
    default:
      return type ?? null;
  }
}

function formatActiveRange(
  beginYear: number | null | undefined,
  endYear: number | null | undefined,
  type: string | null | undefined,
  t: ReturnType<typeof useTranslations<"artist">>,
) {
  const begin = beginYear ? String(beginYear) : null;
  const end = endYear ? String(endYear) : null;

  if (begin && end) return `${begin}-${end}`;
  const date = begin ?? end;
  if (!date) return null;

  const labelType = artistDateLabelType(type);
  return t("activeRange", { type: labelType, bound: begin ? "begin" : "end", date });
}
